import i18next from 'i18next';
import type {
  Approval,
  ApprovalApprover,
  ApprovalAuthor,
  ApprovalComment,
  ApprovalLastOpinionBy,
  ApprovalType,
} from '@/types/approval';
import { Lang, normalizeLang } from '@/utils/approval/i18nText';
import { getApprovalDocUrl } from '@/utils/urls';

/** GET /api/v2/approval 응답의 인물 요약 */
type V2SearchPerson = {
  passportId: string | null;
  employeeNumber: string | null;
  nameKo: string | null;
  nameEn: string | null;
  companyCode?: string | null;
  divisionCode: string | null;
  divisionNameKo?: string | null;
  divisionNameEn?: string | null;
  departmentCode: string | null;
  departmentName: string | null;
  departmentId: string | null;
  positionCode?: string | null;
  positionNameKo?: string | null;
  positionNameEn?: string | null;
  profileImageUrl?: string | null;
};

type V2SearchApprover = V2SearchPerson & {
  type: number;
  order: number | null;
  status: string;
  approvedAt: string | null;
};

type V2SearchLastOpinionBy = V2SearchApprover & {
  opinion?: string | null;
  updatedAt?: string | null;
};

type V2SearchComment = {
  id: string;
  content: string;
  createdBy: {
    passportId: string;
    name: string;
    departmentName: string;
    positionName: string;
    profileImageUrl: string;
    employeeNumber: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type V2SearchHit = {
  documentId: string;
  typeCode: number;
  typeNameKo: string | null;
  typeNameEn: string | null;
  status: string;
  subject: string;
  author: V2SearchPerson | null;
  finalApprover: V2SearchPerson | null;
  currentApprover: V2SearchApprover | null;
  currentApprovers?: V2SearchApprover[] | null;
  opinionCount?: number | null;
  lastOpinionBy?: V2SearchLastOpinionBy | null;
  isBookmarked: boolean;
  isRead: boolean;
  commentCount: number;
  latestComment: V2SearchComment | null;
  authoredAt: string | null;
  latestApprovedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

const pickName = (
  ko: string | null | undefined,
  en: string | null | undefined,
  lang: Lang,
) => (lang === 'en' && en ? en : ko ?? en ?? '');

const toAuthor = (p: V2SearchPerson | null, lang: Lang): ApprovalAuthor => ({
  passportId: p?.passportId ?? '',
  name: pickName(p?.nameKo, p?.nameEn, lang),
  employeeNumber: p?.employeeNumber ?? '',
  companyCode: p?.companyCode ?? '',
  companyName: '',
  placeCode: p?.divisionCode ?? '',
  placeName: pickName(p?.divisionNameKo, p?.divisionNameEn, lang),
  departmentCode: p?.departmentCode ?? '',
  departmentName: p?.departmentName ?? '',
  positionCode: p?.positionCode ?? '',
  positionName: pickName(p?.positionNameKo, p?.positionNameEn, lang),
  profileImageUrl: p?.profileImageUrl ?? '',
});

const toApprover = (
  p: V2SearchPerson | V2SearchApprover | null,
  lang: Lang,
): ApprovalApprover => ({
  passportId: p?.passportId ?? '',
  name: pickName(p?.nameKo, p?.nameEn, lang),
  placeCode: p?.divisionCode ?? '',
  placeName: pickName(p?.divisionNameKo, p?.divisionNameEn, lang),
  departmentCode: p?.departmentCode ?? '',
  departmentName: p?.departmentName ?? '',
  positionCode: p?.positionCode ?? '',
  positionName: pickName(p?.positionNameKo, p?.positionNameEn, lang),
  type: p && 'type' in p ? p.type : 1,
  order: p && 'order' in p ? p.order ?? 0 : 0,
  opinion: null,
  approvedAt: p && 'approvedAt' in p ? p.approvedAt : null,
  createdAt: '',
  updatedAt: '',
});

/** 의견 내용이 없으면 비움 — 넘기면 목록 미리보기가 "이름 직급 : " 로 빈 채 노출 */
const toLastOpinionBy = (
  p: V2SearchLastOpinionBy | null | undefined,
  lang: Lang,
): ApprovalLastOpinionBy | undefined => {
  if (!p?.opinion) return undefined;
  const updatedAt = p.updatedAt ?? p.approvedAt ?? '';
  return {
    ...toApprover(p, lang),
    order: p.order ?? 0,
    status: p.status,
    userId: '',
    employeeNumber: p.employeeNumber ?? '',
    profileImageUrl: p.profileImageUrl ?? '',
    opinion: p.opinion,
    approvedAt: p.approvedAt ?? '',
    createdAt: '',
    updatedAt,
  };
};

const toComment = (c: V2SearchComment): ApprovalComment =>
  ({
    id: c.id,
    content: c.content,
    createdBy: {
      passportId: c.createdBy.passportId,
      detail: {
        name: c.createdBy.name,
        positionName: c.createdBy.positionName,
        departmentName: c.createdBy.departmentName,
        profileImageUrl: c.createdBy.profileImageUrl,
        employeeNumber: c.createdBy.employeeNumber,
      },
    },
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  } as unknown as ApprovalComment);

/** V2 검색 결과 1건을 기존 목록 형태로 변환. 기존 응답(docId 보유)은 그대로 통과 */
export const toLegacyApprovalItem = (
  item: Approval | V2SearchHit,
  tagByCode: Map<number, string>,
  lang: Lang = normalizeLang(i18next.language),
): Approval => {
  if ('docId' in item) return item;
  return {
    docId: item.documentId,
    isBookmark: item.isBookmarked,
    isRead: item.isRead,
    type: {
      code: item.typeCode,
      name: pickName(item.typeNameKo, item.typeNameEn, lang),
      tag: tagByCode.get(item.typeCode) ?? '',
    },
    status: item.status,
    subject: item.subject,
    author: toAuthor(item.author, lang),
    currentApprover: toApprover(item.currentApprover, lang),
    currentApprovers: (item.currentApprovers ?? []).map((a) =>
      toApprover(a, lang),
    ),
    finalApprover: toApprover(item.finalApprover, lang),
    lastOpinionBy: toLastOpinionBy(item.lastOpinionBy, lang),
    opinionCount: item.opinionCount ?? 0,
    commentCount: item.commentCount,
    latestComment: item.latestComment
      ? toComment(item.latestComment)
      : undefined,
    authoredAt: item.authoredAt ?? '',
    latestApprovedAt: item.latestApprovedAt ?? '',
    createdAt: item.createdAt ?? '',
    updatedAt: item.updatedAt ?? '',
    link: getApprovalDocUrl(item.documentId),
  } as Approval;
};

export const toTagByCode = (types: ApprovalType[] | undefined) =>
  new Map((types ?? []).map((t) => [t.type, t.tag]));
